import { Typography } from "@/components";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS } from "@/libraries/constant";
import {
	getCareChatMessagesQueryOptions,
	getCareChatQueryOptions,
} from "@/queries";
import { usePrompt } from "@/stores/prompt";
import type { ResError } from "@/types";
import type { CareChat, CareChatMessage } from "@/types/askhemi";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SendIcon from "@mui/icons-material/Send";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface CaseCareChatProps {
	caseId: number;
	isClosed?: boolean;
}

const CaseCareChat = ({ caseId, isClosed = false }: CaseCareChatProps) => {
	const { t } = useTranslation(["askhemi", "common"]);
	const { profile } = useProfile();
	const openPrompt = usePrompt((state) => state.open);
	const [text, setText] = useState("");
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const bottomRef = useRef<HTMLDivElement>(null);

	const careChatQuery = useQuery(getCareChatQueryOptions(caseId));
	const careChat: CareChat | null = careChatQuery.data?.care_chat ?? null;
	const isOpen = careChat?.status === "open";
	const patientHasAskhemi = careChatQuery.data?.patient_has_askhemi ?? false;

	const messagesQuery = useQuery({
		...getCareChatMessagesQueryOptions(caseId, {
			refetchInterval: isOpen ? 8000 : false,
			enabled: !!careChat,
		}),
	});
	const messages: CareChatMessage[] = messagesQuery.data ?? [];

	const startMutation = useMutation<unknown, ResError, { caseId: number }>({
		mutationKey: [QUERY_KEYS.MUTATE_START_CARE_CHAT],
	});
	const closeMutation = useMutation<unknown, ResError, { caseId: number }>({
		mutationKey: [QUERY_KEYS.MUTATE_CLOSE_CARE_CHAT],
	});
	const sendMutation = useMutation<
		unknown,
		ResError,
		{ caseId: number; text: string }
	>({
		mutationKey: [QUERY_KEYS.MUTATE_SEND_CARE_CHAT_MESSAGE],
	});

	const lastId = messages.at(-1)?.id;
	// biome-ignore lint/correctness/useExhaustiveDependencies: scroll when a new message arrives
	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: "end" });
	}, [lastId, isOpen]);

	const onStart = (reopen = false) => {
		startMutation.mutate(
			{ caseId },
			{
				onSuccess: () =>
					toast.success(reopen ? t("carechat.reopened") : t("carechat.opened")),
				onError: (err) =>
					toast.error(
						err.response?.data?.message ||
							(reopen ? t("carechat.reopenFailed") : t("carechat.startFailed")),
					),
			},
		);
	};

	const onEnd = () => {
		closeMutation.mutate(
			{ caseId },
			{
				onSuccess: () => toast.success(t("carechat.ended")),
				onError: (err) =>
					toast.error(err.response?.data?.message || t("carechat.endFailed")),
			},
		);
	};

	const onRequestEnd = () => {
		setAnchorEl(null);
		openPrompt({
			title: t("carechat.endConfirmTitle"),
			description: t("carechat.endConfirmBody"),
			positiveButton: {
				label: t("carechat.end"),
				color: "error",
				onClick: onEnd,
			},
			negativeButton: {
				label: t("common:cancel"),
				variant: "outlined",
				color: "primary",
			},
		});
	};

	const onSend = (e?: React.FormEvent) => {
		e?.preventDefault();
		const value = text.trim();
		if (!value) {
			toast.error(t("carechat.typeFirst"));
			return;
		}
		sendMutation.mutate(
			{ caseId, text: value },
			{
				onSuccess: () => {
					setText("");
					toast.success(t("carechat.delivered"));
				},
				onError: (err) =>
					toast.error(err.response?.data?.message || t("carechat.sendFailed")),
			},
		);
	};

	return (
		<div className="container flex flex-1 flex-col gap-3 py-3 max-w-3xl">
			<div className="flex items-start gap-2.5 rounded-[11px] bg-[#e6f7ee] p-3 !text-[#0d6b3f]">
				<VisibilityOutlinedIcon
					sx={{ fontSize: 17 }}
					className="mt-0.5 shrink-0"
				/>
				<Typography fontSize={12.5} className="flex-1 leading-[1.5]">
					<b>{t("carechat.bandTitle")}</b> {t("carechat.band")}
				</Typography>
				{isOpen && !isClosed ? (
					<>
						<IconButton
							size="small"
							aria-label={t("carechat.moreActions")}
							onClick={(e) => setAnchorEl(e.currentTarget)}
							className="!text-[#0d6b3f]"
						>
							<MoreVertRoundedIcon fontSize="small" />
						</IconButton>
						<Menu
							anchorEl={anchorEl}
							open={!!anchorEl}
							onClose={() => setAnchorEl(null)}
						>
							<MenuItem onClick={onRequestEnd}>
								<ListItemText>{t("carechat.end")}</ListItemText>
							</MenuItem>
						</Menu>
					</>
				) : null}
			</div>

			{careChatQuery.isLoading ? (
				<div className="h-40 animate-pulse rounded-xl bg-zinc-100" />
			) : careChatQuery.isError ? (
				<Typography fontSize={13} className="!text-red-600">
					{t("carechat.loadFailed")}
				</Typography>
			) : !careChat ? (
				<div className="mx-auto w-full max-w-[330px] px-3 py-[22px] text-center">
					<div className="mx-auto mb-3.5 flex h-16 w-16 items-center justify-center rounded-full bg-[#e6f7ee] text-[#17a05f]">
						<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 30 }} />
					</div>
					<Typography fontWeight={700} fontSize={16} className="!mb-2">
						{t("carechat.notStartedTitle")}
					</Typography>
					<Typography
						fontSize={13.5}
						className="mx-auto !mb-[18px] max-w-[290px] !text-[#667689] leading-[1.55]"
					>
						{t("carechat.notStartedBody")}
					</Typography>
					{!patientHasAskhemi ? (
						<Typography fontSize={12} className="!mb-[18px] !text-amber-700">
							{t("carechat.noAskhemi")}
						</Typography>
					) : null}
					{isClosed ? (
						<Typography fontSize={12} className="!mb-[18px] !text-zinc-500">
							{t("carechat.closedCase")}
						</Typography>
					) : null}
					<button
						type="button"
						disabled={!patientHasAskhemi || startMutation.isPending || isClosed}
						onClick={() => onStart(false)}
						className="w-full rounded-xl bg-[#17a05f] px-4 py-[13px] text-[14.5px] font-bold text-white hover:bg-[#129050] disabled:cursor-not-allowed disabled:opacity-50"
					>
						{startMutation.isPending
							? t("carechat.starting")
							: t("carechat.start")}
					</button>
				</div>
			) : (
				<>
					{!isOpen ? (
						<div className="flex items-start gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-zinc-700">
							<InfoOutlinedIcon fontSize="small" className="mt-0.5" />
							<Typography fontSize={13}>{t("carechat.closedNote")}</Typography>
						</div>
					) : null}
					<div className="flex flex-1 flex-col gap-3 pb-2">
						{messages.length === 0 && !messagesQuery.isLoading ? (
							<Typography
								fontSize={13}
								className="!text-zinc-500 text-center py-6"
							>
								{isOpen
									? t("carechat.noMessages")
									: t("carechat.noMessagesClosed")}
							</Typography>
						) : null}
						{messages.map((m) => {
							const isClinician = m.sender === "clinician";
							const isMe = isClinician && m.sender_name === profile?.fullname;
							return (
								<div
									key={m.id}
									className={cn(
										"flex",
										isClinician ? "justify-end" : "justify-start",
									)}
								>
									<div
										className={cn(
											"max-w-[85%] rounded-xl border p-3",
											isClinician
												? "rounded-tr-none border-emerald-300 bg-emerald-50"
												: "rounded-tl-none border-zinc-200 bg-white",
										)}
									>
										<div className="mb-1 flex flex-wrap items-center gap-2">
											<Typography fontWeight={700} fontSize={13}>
												{m.sender_name}
												{isMe ? ` (${t("carechat.me")})` : ""}
											</Typography>
											<span className="relative flex h-2 w-2">
												<span
													className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
													style={{
														backgroundColor: isClinician
															? "#4C9B65"
															: "#652FD9",
													}}
												/>
												<span
													className="relative inline-flex rounded-full h-2 w-2"
													style={{
														backgroundColor: isClinician
															? "#4C9B65"
															: "#652FD9",
													}}
												/>
											</span>
											{!isClinician ? (
												<span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">
													{t("viaAskHemi")}
												</span>
											) : null}
											<div className="flex items-center gap-1 text-zinc-500">
												<Typography fontSize={11} className="!text-inherit">
													{dayjs(m.created_at).format("DD MMM, hh:mm A")}
												</Typography>
												{isClinician ? (
													<DoneAllOutlinedIcon className="!text-sm !text-success-200" />
												) : null}
											</div>
										</div>
										<Typography
											fontSize={14}
											className="whitespace-pre-wrap break-words"
										>
											{m.text}
										</Typography>
									</div>
								</div>
							);
						})}
						<div ref={bottomRef} />
					</div>
					{isOpen ? (
						<form
							onSubmit={onSend}
							className="sticky bottom-0 w-full container bg-white py-2"
						>
							<div className="relative flex flex-row justify-center items-center gap-3">
								<TextField
									value={text}
									onChange={(e) => setText(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter" && !e.shiftKey) onSend(e);
									}}
									placeholder={t("carechat.placeholder")}
									size="small"
									fullWidth
									multiline
									minRows={1}
									maxRows={4}
									disabled={isClosed}
									className="[&>.MuiInputBase-root]:!rounded-xl"
									slotProps={{
										htmlInput: {
											maxLength: 2000,
											"aria-label": t("carechat.placeholder"),
											style: { resize: "none" },
										},
									}}
								/>
								<IconButton
									type="submit"
									size="large"
									color="primary"
									aria-label={t("carechat.send")}
									disabled={sendMutation.isPending || isClosed}
								>
									<SendIcon />
								</IconButton>
							</div>
						</form>
					) : (
						<div className="sticky bottom-0 flex flex-col items-center gap-2 bg-white py-3">
							{isClosed ? (
								<Typography fontSize={12} className="!text-zinc-500">
									{t("carechat.closedCase")}
								</Typography>
							) : null}
							<button
								type="button"
								disabled={
									!patientHasAskhemi || startMutation.isPending || isClosed
								}
								onClick={() => onStart(true)}
								className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{startMutation.isPending
									? t("carechat.reopening")
									: t("carechat.reopen")}
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default CaseCareChat;
